import React, { useMemo } from "react";
import { useDarkMode } from "../../contexts/DarkModeContext";
import {
  formatDisplayDate,
  formatMoney,
  inferReferenceSubtitleFromNo,
  parseMoney,
  settlementColumnLabels,
  type ReferenceSettlementPartyType,
} from "../../utils/referenceSettlementUtils";

export type ReferenceSettlementViewRecord = {
  id?: string;
  settlement_no?: string;
  settlement_date?: string;
  settlement_amount?: number | string;
  party_id?: string;
  party_name?: string;
  party_type?: string;
  debit_reference_no?: string;
  credit_reference_no?: string;
  pending_reference_no?: string;
  settled_amount?: number | string;
  remarks?: string;
  created_by?: string;
};

interface ReferenceSettlementViewPanelProps {
  formData?: Record<string, any>;
}

const resolvePartyType = (value: unknown): ReferenceSettlementPartyType =>
  String(value ?? "").toLowerCase() === "vendor" ? "vendor" : "customer";

const ReferenceSettlementViewPanel: React.FC<ReferenceSettlementViewPanelProps> = ({
  formData,
}) => {
  const { isDarkMode } = useDarkMode();

  const record = useMemo((): ReferenceSettlementViewRecord => {
    const raw =
      formData?.reference_settlement_view_record ??
      formData?.reference_settlement_view ??
      formData ??
      {};
    return typeof raw === "object" && raw ? (raw as ReferenceSettlementViewRecord) : {};
  }, [formData]);

  const partyType = resolvePartyType(
    record.party_type ?? formData?.party_type
  );
  const partyName =
    String(record.party_name ?? "").trim() ||
    String(
      formData?.[
        partyType === "customer"
          ? "reference_settlement_customer_party_name"
          : "reference_settlement_vendor_party_name"
      ] ?? ""
    ).trim();
  const partyLabel = partyType === "customer" ? "Customer" : "Vendor";
  const columnLabels = settlementColumnLabels(partyType);
  const settlementAmount = parseMoney(
    record.settlement_amount ?? record.settled_amount
  );
  const settlementDate = formatDisplayDate(record.settlement_date);
  const debitRef =
    String(record.debit_reference_no ?? record.pending_reference_no ?? "").trim();
  const creditRef = String(record.credit_reference_no ?? "").trim();

  const cardBg = isDarkMode ? "#1e293b" : "#ffffff";
  const border = isDarkMode ? "#334155" : "#E5E7EB";
  const muted = isDarkMode ? "#94a3b8" : "#64748B";
  const text = isDarkMode ? "#f1f5f9" : "#0F172A";

  const renderSelectedCard = (
    referenceNo: string,
    subtitle: string,
    amount: number,
    dateLabel?: string
  ) => (
    <div
      style={{
        width: "100%",
        textAlign: "left",
        border: "2px solid #34D399",
        borderRadius: 12,
        background: isDarkMode ? "#064e3b33" : "#ecfdf5",
        padding: "14px 16px",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          border: "5px solid #34D399",
          marginTop: 4,
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, color: text, marginBottom: 4 }}>
          {referenceNo || "—"}
        </div>
        <div style={{ fontSize: 13, color: muted }}>
          {subtitle}
          {dateLabel ? ` · ${dateLabel}` : ""}
        </div>
      </span>
      <span style={{ fontWeight: 700, color: text, whiteSpace: "nowrap" }}>
        {formatMoney(amount)}
      </span>
    </div>
  );

  const readOnlyFieldStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${border}`,
    background: isDarkMode ? "#0f172a" : "#f8fafc",
    color: text,
    fontSize: 14,
  };

  return (
    <div
      style={{
        background: cardBg,
        border: `1px solid ${border}`,
        borderRadius: 16,
        padding: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <p style={{ margin: 0, color: muted, fontSize: 14 }}>
            Match any open due with any available credit
          </p>
          {record.settlement_no ? (
            <div style={{ marginTop: 8, fontWeight: 700, color: text, fontSize: 16 }}>
              {record.settlement_no}
            </div>
          ) : null}
        </div>
        {settlementDate ? (
          <div style={{ fontSize: 14, color: muted }}>
            Settlement date:{" "}
            <strong style={{ color: text }}>{settlementDate}</strong>
          </div>
        ) : null}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(220px, 320px) 1fr",
          gap: 16,
          alignItems: "end",
          marginBottom: 18,
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: text,
              marginBottom: 6,
            }}
          >
            {partyLabel}
          </label>
          <div style={readOnlyFieldStyle}>{partyName || "—"}</div>
        </div>
        <div style={{ color: muted, fontSize: 14, paddingBottom: 8 }}>
          {partyName ? (
            <>
              {partyLabel}{" "}
              <strong style={{ color: text }}>{partyName}</strong>
              {settlementDate ? (
                <>
                  {" · as on "}
                  {settlementDate}
                </>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ fontWeight: 700, color: text, marginBottom: 10 }}>
            {columnLabels.leftTitle}
          </div>
          {renderSelectedCard(
            debitRef,
            inferReferenceSubtitleFromNo(debitRef),
            settlementAmount,
            settlementDate
          )}
        </div>
        <div>
          <div style={{ fontWeight: 700, color: text, marginBottom: 10 }}>
            {columnLabels.rightTitle}
          </div>
          {renderSelectedCard(
            creditRef,
            inferReferenceSubtitleFromNo(creditRef),
            settlementAmount,
            settlementDate
          )}
        </div>
      </div>

      <div
        style={{
          paddingTop: 20,
          borderTop: `1px solid ${border}`,
          display: "grid",
          gridTemplateColumns: "180px 1fr",
          gap: 12,
          alignItems: "start",
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: text,
              marginBottom: 6,
            }}
          >
            Settlement amount
          </label>
          <div style={readOnlyFieldStyle}>{formatMoney(settlementAmount)}</div>
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: text,
              marginBottom: 6,
            }}
          >
            Remarks
          </label>
          <div style={{ ...readOnlyFieldStyle, minHeight: 42 }}>
            {String(record.remarks ?? "").trim() || "—"}
          </div>
        </div>
      </div>

      {record.created_by ? (
        <div style={{ marginTop: 16, fontSize: 13, color: muted }}>
          Created by <strong style={{ color: text }}>{record.created_by}</strong>
        </div>
      ) : null}
    </div>
  );
};

export default ReferenceSettlementViewPanel;
